import React, { useEffect, useState, useRef } from "react";
import {
  AddCircleOutline,
  Delete,
  Edit,
  Visibility
} from "@mui/icons-material";
import { Button, IconButton, Tooltip, Switch } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import "../../styles/styles.css";
import "../../styles/layout.css";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import ProjectFormModal from "./AddProjectForm";
import ProjectViewModal from "./ProjectView";
import ReusableGrid from "../../components/ag grid/ReusableGrid"; // Your ag grid wrapper component
import ExportButton from "../../components/ag grid/ExportButton";
import usePermission from "../../utils/usePermission";

const ProjectList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const { getProjects, deleteProject, updateProject } = ProjectViewController();
  const { projects } = useSelector((state) => state.project);

  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const { hasPermission } = usePermission();

  useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  const handleDelete = (projectId) => {
    dispatch(deleteProject(projectId));
  };

  const handleEdit = (project) => {
    setEditProject(project);
    setShowModal(true);
  };

  const handleView = (project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const openModalForCreate = () => {
    setEditProject(null);
    setShowModal(true);
  };

  const handleStatusToggle = (params) => {
    const updatedValue = !params.data.is_active;
    const updatedData = { ...params.data, is_active: updatedValue };

    // Update UI instantly
    params.node.setDataValue("is_active", updatedValue);

    // Dispatch update API call
    dispatch(updateProject(params.data.id, updatedData))
      .then(() => {
        console.log("Project status updated");
      })
      .catch((err) => {
        console.error("Failed to update project status", err);
      });
  };

  const columnDefs = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      pinned: "left",
      maxWidth: 50
    },
    {
      headerName: "ID",
      valueGetter: (params) => params.node.rowIndex + 1,
      maxWidth: 70
    },
    {
      headerName: "Title",
      field: "title",
      flex: 1
    },
    {
      headerName: "Start Date",
      field: "start_date",
      flex: 1
    },
    {
      headerName: "End Date",
      field: "end_date",
      flex: 1
    },
    {
      headerName: "Status",
      field: "is_active",
      flex: 0.7,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Switch
            checked={params.value}
            onChange={() => handleStatusToggle(params)}
            color="primary"
            size="small"
          />
        </div>
      )
    },
    {
      headerName: "Actions",
      pinned: "right",
      minWidth: 140,
      maxWidth: 180,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <Tooltip title="View">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleView(params.data)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {hasPermission("project_list", "edit") && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(params.data)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission("project_list", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: "#d32f2f" }}
                onClick={() => handleDelete(params.data.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      <div
        className="d-flex flex-wrap gap-2 mb-3"
        style={{ justifyContent: "space-between" }}
      >
        <div className="mt-2">
          {hasPermission("project_list", "create") && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={openModalForCreate}
              sx={{
                borderRadius: "8px",
                backgroundColor: "#1976d2",
                textTransform: "none",
                paddingX: 2,
                height: "40px",
                fontSize: "0.875rem"
              }}
            >
              Add Project
            </Button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div>
            {hasPermission("project_list", "export") && (
              <ExportButton
                gridRef={gridRef}
                fileName="projects.xlsx"
                exportColumns={["title", "start_date", "end_date", "is_active"]}
                disabled={selectedRows.length === 0}
              />
            )}
          </div>
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </div>
      </div>

      {/* <div className="label-card">
        <Row className="justify-content-between w-100">
          <Col>
            <h3>Project List</h3>
          </Col>
        </Row>
      </div> */}

      <div className="ag-theme-material" style={{ height: "70vh" }}>
        <ReusableGrid
          rowData={projects}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
        />
      </div>

      <ProjectFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        initialData={editProject}
      />

      <ProjectViewModal
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        selectedProject={selectedProject}
      />
    </div>
  );
};

export default ProjectList;
